import type { FC, ReactNode } from "react";
import { useMemo } from "react";

import classNames from "classnames";

import { PinDefaultIcon } from "@jti/brand-icons/pin-default";
import { Button } from "@jti/core-components/components/Button";
import { ImageBehaviour } from "@jti/core-components/components/ImageBehaviour";
import type { LinkBehaviourProps } from "@jti/core-components/components/LinkBehaviour";
import { DEPRECATED_IconButton } from "@jti/ui/components/DEPRECATED_IconButton";

import styles from "./CardSensorialExperiance.module.scss";

export type CardSensorialExperianceProps = {
  variant?: "default" | "popup";
  imageUrl?: string;
  getDirectionUrl: string;
  getDirectionLabel: string;
  redeemButtonLabel?: string;
  handleVenueClick?: () => void;
  handleRedeemClick?: (arg: any) => void;
  description: string;
  linkLabel: string;
  title: string;
  points?: string;
  isAvailable?: boolean;
  children?: ReactNode;
  discoverButtonUrl?: string;
  discoverButtonText?: string;
} & Partial<Pick<LinkBehaviourProps, "rel" | "target">>;

export const CardSensorialExperiance: FC<CardSensorialExperianceProps> = ({
  variant = "default",
  imageUrl,
  getDirectionUrl,
  handleVenueClick,
  handleRedeemClick,
  isAvailable,
  points,
  title,
  description,
  linkLabel,
  children,
  getDirectionLabel,
  redeemButtonLabel,
  rel,
  target,
  discoverButtonText,
  discoverButtonUrl,
}) => {
  const isDefaultVariant = variant === "default";
  const isPrimaryButtonShown = isAvailable && isDefaultVariant;
  const isDiscoverButtonShown = discoverButtonUrl && discoverButtonText && isDefaultVariant;
  const areButtonsDisplayed = isPrimaryButtonShown || isDiscoverButtonShown;
  const arePointsDisplayed = points && isDefaultVariant;
  const directionLinkProps: LinkBehaviourProps = useMemo(
    () => ({
      href: getDirectionUrl,
      children: getDirectionLabel,
      rel,
      target,
    }),
    [getDirectionLabel, getDirectionUrl, rel, target]
  );

  return (
    <div
      className={classNames(styles.contaier, {
        [styles.popup]: !isDefaultVariant,
      })}
    >
      {imageUrl && (
        <div className={styles.imageWrapper}>
          <ImageBehaviour alt={linkLabel} src={imageUrl} />
          {children}
        </div>
      )}
      <div className={styles.contentWrapper}>
        <div className={styles.textWrapper}>
          <span className={styles.title}>{title}</span>
          {arePointsDisplayed && <span className={styles.text}>{points}</span>}
        </div>
        <div className={styles.textWrapper}>
          <Button size="lg" variant="text" onClick={handleVenueClick}>
            {linkLabel}
          </Button>
          <p className={classNames(styles.text, styles.popupDescription)}>
            {description}
            {!isDefaultVariant && directionLinkProps.href && (
              <DEPRECATED_IconButton {...directionLinkProps} icon={PinDefaultIcon} />
            )}
          </p>
        </div>
      </div>
      {isDefaultVariant && directionLinkProps.href && (
        <DEPRECATED_IconButton {...directionLinkProps} icon={PinDefaultIcon} />
      )}
      {areButtonsDisplayed ? (
        <div className={styles.buttons}>
          {isPrimaryButtonShown && (
            <Button variant="primary" size="sm" onClick={handleRedeemClick}>
              {redeemButtonLabel}
            </Button>
          )}
          {isDiscoverButtonShown && (
            <Button variant="secondary" size="sm" href={discoverButtonUrl}>
              {discoverButtonText}
            </Button>
          )}
        </div>
      ) : null}
    </div>
  );
};
